import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { StatusBadge, TypeBadge } from "./Badges";
import { formatAcres, relTime } from "../lib/incidents";
import { MapPin, FireSimple, ArrowFatUp, Lightning, BellRinging, Broadcast, ShareNetwork } from "@phosphor-icons/react";
import InlineShareButton from "./InlineShareButton";
import FollowFireButton from "./FollowFireButton";
import { shareOrigin } from "../lib/shareOrigin";
import { buildIncidentCaption, evacsForShare } from "../lib/shareCaption";
import { markFollowSeen, hasFollowUpdate } from "../lib/followedFires";
import { api } from "../lib/api";

const PROMO_FRESH_MS = 24 * 60 * 60 * 1000; // 24h pulse window

function isFreshlyPromoted(iso) {
  if (!iso) return false;
  try {
    return Date.now() - new Date(iso).getTime() < PROMO_FRESH_MS;
  } catch {
    return false;
  }
}

function GrowthSparkline({ points, testid }) {
  if (!points || points.length < 2) return null;
  const vals = points.map((p) => p.acres || 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (max <= 0) return null;
  const w = 56, h = 16;
  const xs = points.map((_, i) => 1 + (i / (points.length - 1)) * (w - 4));
  const ys = vals.map((v) => h - 2 - ((v - min) / (max - min || 1)) * (h - 4));
  const rising = vals[vals.length - 1] > vals[0];
  const color = rising ? "#F2542D" : "#7B8A83";
  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="shrink-0"
      data-testid={testid}
    >
      <title>7-day growth: {Math.round(min).toLocaleString()} → {Math.round(max).toLocaleString()} ac</title>
      <polyline
        points={xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ")}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r="1.8" fill={color} />
    </svg>
  );
}

function fmtStamp(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return "";
  }
}

export default function IncidentCard({ incident, onHover, active, bestAcres, history, converging = false, evacAlerts = [], lastSharedAt = null, lightning = null, followed = false }) {
  const promoted = isFreshlyPromoted(incident.promoted_at);
  const followUpdate = followed && hasFollowUpdate(incident.id, incident.date);
  const acresVal = bestAcres ? bestAcres.acres : incident.acres;
  const acresAt = history?.length ? history[history.length - 1].at : incident.date;
  const acresSuffix =
    bestAcres?.source === "mapped" ? " (mapped)" : bestAcres?.source === "nifc" ? " (NIFC)" : "";
  const shareUrl = `${shareOrigin()}/api/share/incident/${incident.id}`;
  // Lazily pull any cached AI summary when the share menu first opens —
  // cached_only never triggers an LLM generation from the feed.
  const [aiSummary, setAiSummary] = useState(null);
  const summaryFetched = useRef(false);
  const fetchSummary = () => {
    if (summaryFetched.current) return;
    summaryFetched.current = true;
    api
      .get(`/incidents/${incident.id}/ai-summary`, { params: { cached_only: true } })
      .then((r) => { if (r.data?.summary) setAiSummary(r.data.summary); })
      .catch(() => {});
  };
  const shareCaption = useMemo(
    () =>
      buildIncidentCaption({
        name: incident.name,
        county: incident.county,
        acres: acresVal,
        statusLabel: incident.status_label,
        incNum: incident.inc_num,
        resources: incident.resources,
        evacs: evacsForShare(evacAlerts, incident.latitude, incident.longitude, incident.name),
        lastSharedAt,
        updatedAt: incident.date,
        url: shareUrl,
        summary: aiSummary,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [incident, acresVal, evacAlerts, lastSharedAt, aiSummary]
  );
  return (
    <Link
      to={`/incident/${incident.id}`}
      data-testid={`incident-card-${incident.id}`}
      onMouseEnter={() => onHover && onHover(incident.id)}
      onMouseLeave={() => onHover && onHover(null)}
      onClick={() => { if (followed) markFollowSeen(incident.id); }}
      className={`relative block border-l-2 px-4 py-3 transition-colors border-b border-b-tac-border/60 ${
        active
          ? "bg-tac-primary/15 border-l-tac-primary ring-1 ring-inset ring-tac-primary/50 card-active-flash"
          : promoted
          ? "bg-tac-primary/8 border-l-tac-primary animate-promote-pulse"
          : "border-l-transparent hover:bg-tac-surface/70 hover:border-l-tac-borderBright"
      }`}
    >
      {promoted && (
        <span
          data-testid={`promoted-badge-${incident.id}`}
          className="absolute top-2 right-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-bold uppercase tracking-wider bg-tac-primary text-white shadow-md"
          title={`Promoted from ${incident.promoted_from_type || "smoke check"} to wildfire`}
        >
          <ArrowFatUp size={9} weight="fill" /> Escalated
        </span>
      )}

      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <TypeBadge type={incident.type} />
          <StatusBadge status={incident.status_label} />
          {followUpdate && (
            <span
              data-testid={`follow-update-badge-${incident.id}`}
              title="New activity on a fire you follow — opens clear the signal"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-[#4CC9F0]/70 bg-[#4CC9F0]/15 text-[#4CC9F0] animate-pulse"
            >
              <BellRinging size={9} weight="fill" /> Update
            </span>
          )}
          {converging && (
            <span
              data-testid={`converging-badge-${incident.id}`}
              title="This fire's perimeter is within 12 mi of another large fire — possible complex"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-tac-warning/70 bg-tac-warning/15 text-tac-warning"
            >
              <FireSimple size={9} weight="fill" /> Converging
            </span>
          )}
          {lightning && (
            <span
              data-testid={`lightning-badge-${incident.id}`}
              title={`${lightning.strikes} lightning strike${lightning.strikes === 1 ? "" : "s"} within 3 mi in the 72h before this fire was reported (nearest ${lightning.nearest_mi} mi)`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-[#7DD3FC]/70 bg-[#7DD3FC]/15 text-[#7DD3FC]"
            >
              <Lightning size={9} weight="fill" /> Lightning
            </span>
          )}
          {lastSharedAt && (
            <span
              data-testid={`shared-indicator-${incident.id}`}
              title={`This fire was last shared ${relTime(lastSharedAt)}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-tac-safe/60 bg-tac-safe/10 text-tac-safe"
            >
              <ShareNetwork size={9} weight="fill" /> Shared {relTime(lastSharedAt)}
            </span>
          )}
          {incident.satellite_heat && (
            <span
              data-testid={`sat-heat-badge-${incident.id}`}
              title={`NASA VIIRS detected ${incident.satellite_heat.count} heat point${incident.satellite_heat.count === 1 ? "" : "s"} within 4 mi in the last 6 hours — fire confirmed active by satellite even if dispatch hasn't updated`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-orange-400/70 bg-orange-400/15 text-orange-400"
            >
              <Broadcast size={9} weight="fill" /> Sat Heat
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          {!promoted && (
            <span className="text-[15px] font-mono-tac text-tac-faint whitespace-nowrap">
              {relTime(incident.date)}
            </span>
          )}
          <FollowFireButton incidentId={incident.id} compact />
          <InlineShareButton
            variant="compact"
            testid={`share-incident-${incident.id}`}
            url={shareUrl}
            previewPath={`/incident/${incident.id}/share`}
            navigateTo={`/incident/${incident.id}`}
            title={`${incident.name} — Firewatch`}
            onOpen={fetchSummary}
            caption={shareCaption}
            text={
              `${incident.type || "Incident"} · ${incident.name}` +
              (acresVal != null ? ` · ${formatAcres(acresVal)} ac` : "") +
              " · via Okanogan Highlands Firewatch"
            }
          />
        </div>
      </div>

      <h3 className="font-display font-bold text-[16px] text-tac-text leading-snug mb-1.5 line-clamp-2">
        {incident.name}
      </h3>

      <div className="flex items-center gap-4 text-[15px] text-tac-dim flex-wrap">
        {incident.location && (
          <span className="inline-flex items-center gap-1">
            <MapPin size={11} weight="fill" className="text-tac-faint" />
            <span className="font-mono-tac tabular">{incident.location}</span>
          </span>
        )}
        <span className="inline-flex items-center gap-1">
          <FireSimple size={11} weight="fill" className="text-tac-primary" />
          <span className="font-mono-tac tabular">{formatAcres(acresVal)} ac{acresSuffix}</span>
          {acresVal != null && fmtStamp(acresAt) && (
            <span
              data-testid={`acres-updated-${incident.id}`}
              title="When this acreage figure was last recorded"
              className="font-mono-tac tabular text-tac-faint text-[11px]"
            >
              · upd {fmtStamp(acresAt)}
            </span>
          )}
        </span>
        <GrowthSparkline points={history} testid={`spark-${incident.id}`} />
        {incident.inc_num && (
          <span className="font-mono-tac tabular text-tac-faint">
            #{incident.inc_num}
          </span>
        )}
        {promoted && incident.promoted_from_type && (
          <span className="font-mono-tac tabular text-tac-primary">
            ← was {incident.promoted_from_type}
          </span>
        )}
      </div>
    </Link>
  );
}
